import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  getLists,
  renameList,
  deleteList,
  exportListsJson,
  importListsJson,
} from "@/lib/lists";
import { ListChecks, Trash2, Copy, Download, Upload, Check, X } from "lucide-react";

export default function ManageListsDialog({ open, onOpenChange, onChange }) {
  const [tick, setTick] = useState(0);
  const lists = useMemo(() => getLists(), [tick, open]);
  const names = Object.keys(lists).sort((a, b) => a.localeCompare(b));

  const [editing, setEditing] = useState(null); // name being renamed
  const [editValue, setEditValue] = useState("");
  const [importing, setImporting] = useState(false);
  const [importText, setImportText] = useState("");

  useEffect(() => {
    if (!open) {
      setEditing(null);
      setEditValue("");
      setImporting(false);
      setImportText("");
    }
  }, [open]);

  const refresh = () => {
    setTick((t) => t + 1);
    onChange?.();
  };

  const startRename = (n) => {
    setEditing(n);
    setEditValue(n);
  };

  const commitRename = () => {
    const next = editValue.trim();
    if (!next) {
      setEditing(null);
      return;
    }
    const ok = renameList(editing, next);
    if (!ok) {
      toast.error("A list with that name already exists");
      return;
    }
    toast.success("List renamed");
    setEditing(null);
    refresh();
  };

  const doDelete = (n) => {
    if (!window.confirm(`Delete "${n}"?`)) return;
    deleteList(n);
    toast.success("List deleted");
    refresh();
  };

  const exportAll = async () => {
    const json = exportListsJson();
    try {
      await navigator.clipboard.writeText(json);
      toast.success("Lists copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const doImport = () => {
    if (!importText.trim()) {
      setImporting(false);
      return;
    }
    try {
      importListsJson(importText);
      toast.success("Lists imported");
      setImporting(false);
      setImportText("");
      refresh();
    } catch (err) {
      toast.error(err.message || "Import failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-pbo-cream text-pbo-ink border-pbo-border max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="manage-lists-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-pbo-green text-2xl flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-pbo-gold" />
            Manage personal lists
          </DialogTitle>
          <p className="text-xs text-pbo-muted">
            Lists are saved in this browser. Use export/import to sync across devices.
          </p>
        </DialogHeader>

        <div className="space-y-3 mt-3">
          {names.length === 0 ? (
            <div className="text-sm text-pbo-muted italic py-6 text-center bg-white border border-pbo-border rounded-lg">
              No saved lists yet. Save your current field in the create form.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="lists-list">
              {names.map((n) => (
                <li
                  key={n}
                  className="bg-white border border-pbo-border rounded-lg px-3 py-2 flex items-center gap-2"
                >
                  {editing === n ? (
                    <>
                      <Input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value.slice(0, 60))}
                        className="h-8 text-sm"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitRename();
                          if (e.key === "Escape") setEditing(null);
                        }}
                        data-testid="rename-input"
                      />
                      <Button
                        size="sm"
                        onClick={commitRename}
                        className="h-8 bg-pbo-green text-pbo-cream hover:bg-pbo-green-deep"
                        data-testid="rename-confirm-btn"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing(null)}
                        className="h-8 text-pbo-muted"
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => startRename(n)}
                          className="text-left font-semibold text-pbo-green hover:text-pbo-gold truncate w-full"
                          data-testid={`list-name-${n}`}
                        >
                          {n}
                        </button>
                        <div className="text-[11px] text-pbo-muted font-mono">
                          {lists[n].length} golfer{lists[n].length === 1 ? "" : "s"}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => doDelete(n)}
                        aria-label={`Delete ${n}`}
                        title="Delete"
                        className="text-pbo-muted hover:text-pbo-green p-1.5 rounded"
                        data-testid={`delete-list-${n}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* Import / Export */}
          <div className="pt-4 border-t border-pbo-border space-y-2">
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={exportAll}
                disabled={names.length === 0}
                className="border-pbo-border text-pbo-green hover:bg-pbo-paper text-xs"
                data-testid="export-lists-btn"
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" />
                Copy all (export JSON)
              </Button>
              <Button
                variant="outline"
                onClick={() => setImporting((v) => !v)}
                className="border-pbo-border text-pbo-green hover:bg-pbo-paper text-xs"
                data-testid="toggle-import-btn"
              >
                <Upload className="w-3.5 h-3.5 mr-1.5" />
                {importing ? "Cancel import" : "Paste JSON to import"}
              </Button>
            </div>
            {importing && (
              <div className="space-y-2 fade-in">
                <Textarea
                  rows={5}
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  placeholder='{"My List": ["Tiger", "Phil"]}'
                  className="bg-white font-mono text-xs"
                  data-testid="import-textarea"
                />
                <Button
                  onClick={doImport}
                  className="bg-pbo-gold text-pbo-ink hover:bg-pbo-gold-soft text-xs h-9"
                  data-testid="import-confirm-btn"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  Import (merge into existing)
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-pbo-border text-pbo-muted"
            data-testid="manage-lists-close-btn"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
